import {
  addLinkedProperties,
  cleanInputUpdate,
  CONTACT_TABLE_NAME,
  createEntityRelation as createMutation,
  CreateEntityRelationMutationVariables,
  deleteEntityRelation as deleteMutation,
  DeleteEntityRelationMutationVariables,
  EntityRelation,
  getEntityRelation as getQuery,
  getReadId,
  getTableClientId,
  Model,
  OnCreateEntityRelationSubscription,
  OnDeleteEntityRelationSubscription,
  OnUpdateEntityRelationSubscription,
  onCreateEntityRelation,
  onDeleteEntityRelation,
  onUpdateEntityRelation,
  syncEntityRelations as syncEntityRelationsQuery,
  UpdateEntityRelationInput,
  updateEntityRelation as updateMutation,
} from '@rentguru/commons-utils';
import {
  deleteEntityWithFetchBefore,
  getFilterFieldNameForIndex,
  list,
  mutation,
  mutationWithFetchBefore,
  recordWasUpdated,
  useSubscriptions,
} from '@up2rent/graphql';
import isEqual from 'lodash/isEqual';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import { useUser } from './UserContext';

const ENTITY_MODEL_NAME: Model = 'EntityRelation';

type CreateEntityRelation = Omit<EntityRelation, 'clientId' | 'readId'>;

export interface EntityRelationsContext {
  loading: boolean;
  entityRelations: EntityRelation[];
  getEntityRelations: () => EntityRelation[];
  createEntityRelation: (input: CreateEntityRelation) => Promise<EntityRelation>;
  updateEntityRelation: (original: EntityRelation, updates: UpdateEntityRelationInput) => Promise<EntityRelation>;
  deleteEntityRelation: (entityRelation: EntityRelation) => Promise<EntityRelation>;
  syncLinkedEntities: (originalRelations: EntityRelation[], newRelations: CreateEntityRelation[]) => Promise<void>;
}

export const EntityRelationContext = React.createContext<EntityRelationsContext | null>(null);

export const fetchEntityRelations = async (clientId: string): Promise<EntityRelation[]> => {
  const baseQuery = addLinkedProperties(syncEntityRelationsQuery);
  return await list<EntityRelation>(
    baseQuery,
    getFilterFieldNameForIndex('byClientId'),
    getTableClientId(clientId, ENTITY_MODEL_NAME)
  );
};

export const EntityRelationsContextProvider = ({ children }: { children?: React.ReactNode }) => {
  const { clientId } = useUser();
  const [entityRelations, setEntityRelations] = useState<EntityRelation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!clientId) return;
    let unmounted = false;
    fetchEntityRelations(clientId)
      .then((result) => {
        if (!unmounted) {
          setEntityRelations(result);
          setLoading(false);
        }
      })
      .catch((error) => {
        console.error('[entityRelations] fetching entity relations failed', error);
        if (!unmounted) {
          setLoading(false);
        }
      });
    return () => {
      unmounted = true;
    };
  }, [clientId]);

  useSubscriptions<
    OnCreateEntityRelationSubscription,
    OnUpdateEntityRelationSubscription,
    OnDeleteEntityRelationSubscription
  >(
    onCreateEntityRelation,
    onUpdateEntityRelation,
    onDeleteEntityRelation,
    (data) => {
      const newRelation = data.onCreateEntityRelation as EntityRelation;
      setEntityRelations((currentRelations) => {
        if (!currentRelations || currentRelations.some((relation) => relation.id === newRelation.id))
          return currentRelations;
        return [...currentRelations, newRelation];
      });
    },
    (data) => {
      const updated = data.onUpdateEntityRelation as EntityRelation;
      setEntityRelations((currentRelations) => {
        if (!currentRelations) return currentRelations;
        const current = currentRelations.find((relation) => relation.id === updated.id);
        if (!current) return [...currentRelations, updated];
        if (!recordWasUpdated(current, updated)) return currentRelations;
        return currentRelations.map((relation) => (relation.id === updated.id ? updated : relation));
      });
    },
    (data) => {
      const { id } = data.onDeleteEntityRelation as EntityRelation;
      setEntityRelations(
        (currentRelations) => currentRelations?.filter((relation) => relation.id !== id) ?? currentRelations
      );
    }
  );

  const getEntityRelations = (): EntityRelation[] => entityRelations;

  const createEntityRelation = async (input: CreateEntityRelation): Promise<EntityRelation> => {
    return await mutation<EntityRelation, CreateEntityRelationMutationVariables>(createMutation, {
      input: {
        sourceId: input.sourceId,
        sourceTableName: input.sourceTableName ?? CONTACT_TABLE_NAME,
        foreignId: input.foreignId,
        foreignTableName: input.foreignTableName ?? CONTACT_TABLE_NAME,
        linkDescription: input.linkDescription,
        linkProperties: input.linkProperties,
        clientId: getTableClientId(clientId!, ENTITY_MODEL_NAME),
        readId: getReadId(clientId!, ENTITY_MODEL_NAME),
      },
    });
  };

  const updateEntityRelation = async (
    original: EntityRelation,
    updates: Partial<UpdateEntityRelationInput>
  ): Promise<EntityRelation> =>
    await mutationWithFetchBefore<EntityRelation>(original.id, getQuery, updateMutation, {
      input: cleanInputUpdate({ id: original.id, _version: original._version, ...updates }, false),
    });

  const deleteEntityRelation = async (entityRelation: EntityRelation): Promise<EntityRelation> =>
    await deleteEntityWithFetchBefore<EntityRelation, DeleteEntityRelationMutationVariables>(
      entityRelation,
      getQuery,
      deleteMutation
    );

  const areRelationsEqual = (relationA?: CreateEntityRelation, relationB?: EntityRelation) => {
    return (
      relationA?.sourceId === relationB?.sourceId &&
      relationA?.sourceTableName === relationB?.sourceTableName &&
      relationA?.foreignId === relationB?.foreignId &&
      relationA?.foreignTableName === relationB?.foreignTableName &&
      relationA?.linkDescription === relationB?.linkDescription &&
      isEqual(relationA?.linkProperties ?? {}, relationB?.linkProperties ?? {})
    );
  };

  const syncLinkedEntities = async (
    originalRelations: EntityRelation[],
    newRelations: CreateEntityRelation[]
  ): Promise<void> => {
    if (!clientId) return;

    const toDelete = originalRelations.filter(
      (relation) => !newRelations.some((newCurrentRelation) => newCurrentRelation.id === relation.id)
    );
    const toCreate = newRelations.filter(
      (relation) => relation.id && !originalRelations.some((originalRelation) => originalRelation.id === relation.id)
    );
    const toUpdate = newRelations.filter((relation) => {
      const original = originalRelations.find((originalRelation) => originalRelation.id === relation.id);
      return original && !areRelationsEqual(relation, original);
    });

    await Promise.all([
      ...toDelete.map((relation) => deleteEntityRelation(relation)),
      ...toCreate.map((relation) => createEntityRelation(relation)),
      ...toUpdate.map((relation) => {
        const original = originalRelations.find((originalRelation) => originalRelation.id === relation.id)!;
        return updateEntityRelation(original, relation);
      }),
    ]);
  };

  const values = useMemo(
    () => ({
      loading,
      entityRelations,
      getEntityRelations,
      createEntityRelation,
      updateEntityRelation,
      deleteEntityRelation,
      syncLinkedEntities,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, entityRelations, clientId]
  );

  return <EntityRelationContext.Provider value={values}>{children}</EntityRelationContext.Provider>;
};

export const useEntityRelations = (): EntityRelationsContext => {
  const context = useContext(EntityRelationContext);
  if (!context)
    throw new Error('`useEntityRelations` hook must be used within a `EntityRelationsContextProvider` component');
  return context;
};
